import {
  type ComicPageGenerationSnapshot,
  normalizePageGenerationSnapshot,
} from './comicPageGenerationSnapshot'
import { shouldRequirePageSelection } from './comicPageImageStatus'
import type { ComicPage, ComicPageCandidate, ComicPageImageStatus, ComicProject } from './comicTypes'
import { randomUuid } from './randomUuid'

export function syncPageImageFromSelection(page: ComicPage): ComicPage {
  const selected = page.pageCandidates.find((c) => c.id === page.selectedPageCandidateId)
  const url = selected?.imageUrl ?? null

  if (page.pageImageStatus === 'failed') {
    return {
      ...page,
      pageImageUrl: page.pageImageUrl ?? null,
      pageImageStatus: 'failed',
    }
  }

  if (page.pageCandidates.length > 0) {
    return {
      ...page,
      pageImageUrl: url,
      pageImageStatus: url ? 'done' : 'pending_selection',
      pageErrorMessage: undefined,
    }
  }

  if (page.pageImageStatus === 'generating') {
    return {
      ...page,
      pageImageUrl: page.pageImageUrl ?? null,
      pageImageStatus: 'generating',
    }
  }

  return {
    ...page,
    pageImageUrl: url,
    pageImageStatus: page.pageImageStatus ?? 'draft',
  }
}

export function normalizePageCandidates(
  dto: {
    pageCandidates?: {
      id?: string
      imageUrl?: string
      creationTaskId?: string | null
      createdAt?: string
      generation?: unknown
    }[]
    selectedPageCandidateId?: string | null
    pageImageUrl?: string | null
    pageImageStatus?: string
  },
): Pick<ComicPage, 'pageCandidates' | 'selectedPageCandidateId' | 'pageImageUrl' | 'pageImageStatus'> {
  const mapped: ComicPageCandidate[] = (dto.pageCandidates ?? [])
    .filter((c) => c?.imageUrl)
    .map((c) => {
      const generation = normalizePageGenerationSnapshot(c.generation)
      const candidate: ComicPageCandidate = {
        id: c.id && String(c.id) ? String(c.id) : randomUuid(),
        imageUrl: String(c.imageUrl),
        creationTaskId: c.creationTaskId ?? null,
        createdAt: c.createdAt ?? new Date().toISOString(),
      }
      if (generation) candidate.generation = generation
      return candidate
    })

  if (mapped.length === 0 && dto.pageImageUrl) {
    mapped.push({
      id: randomUuid(),
      imageUrl: dto.pageImageUrl,
      creationTaskId: null,
      createdAt: new Date().toISOString(),
    })
  }

  const selectedPageCandidateId =
    dto.selectedPageCandidateId && mapped.some((c) => c.id === dto.selectedPageCandidateId)
      ? dto.selectedPageCandidateId
      : null

  const base: ComicPage = {
    id: '',
    index: 0,
    title: '',
    layout: '1x1',
    panelIds: [],
    pageCandidates: mapped,
    selectedPageCandidateId,
    pageImageUrl: dto.pageImageUrl ?? null,
    pageImageStatus: normalizePageImageStatusFromDto(dto.pageImageStatus),
  }
  const synced = syncPageImageFromSelection(base)
  const pageImageStatus = resolvePageImageStatusFromDto(
    dto.pageImageStatus,
    synced.pageImageStatus ?? 'draft',
  )
  return {
    pageCandidates: mapped,
    selectedPageCandidateId: synced.selectedPageCandidateId ?? null,
    pageImageUrl: synced.pageImageUrl ?? null,
    pageImageStatus,
  }
}

function normalizePageImageStatusFromDto(raw: unknown): ComicPageImageStatus {
  if (
    raw === 'generating' ||
    raw === 'done' ||
    raw === 'failed' ||
    raw === 'pending_selection'
  ) {
    return raw
  }
  return 'draft'
}

function resolvePageImageStatusFromDto(
  raw: unknown,
  derived: ComicPageImageStatus,
): ComicPageImageStatus {
  const explicit = normalizePageImageStatusFromDto(raw)
  if (explicit === 'generating' && (derived === 'done' || derived === 'pending_selection')) {
    return derived
  }
  if (explicit !== 'draft') {
    return explicit
  }
  return derived
}

export function appendPageCandidates(
  project: ComicProject,
  episodeId: string,
  pageId: string,
  imageUrls: string[],
  creationTaskId: string | null,
  maxStored: number,
  candidatesPerGeneration: number,
  generation?: ComicPageGenerationSnapshot,
): ComicProject {
  if (imageUrls.length === 0) return project
  return {
    ...project,
    episodes: project.episodes.map((ep) => {
      if (ep.id !== episodeId) return ep
      return {
        ...ep,
        pages: ep.pages.map((page) => {
          if (page.id !== pageId) return page
          const now = new Date().toISOString()
          const added: ComicPageCandidate[] = imageUrls.map((url) => {
            const candidate: ComicPageCandidate = {
              id: randomUuid(),
              imageUrl: url,
              creationTaskId,
              createdAt: now,
            }
            if (generation) candidate.generation = generation
            return candidate
          })
          let pageCandidates = [...(page.pageCandidates ?? []), ...added]
          if (pageCandidates.length > maxStored) {
            pageCandidates = pageCandidates.slice(pageCandidates.length - maxStored)
          }

          const requireSelection = shouldRequirePageSelection(candidatesPerGeneration)
          if (!requireSelection && imageUrls.length >= 1) {
            const only = pageCandidates.at(-1)!
            return syncPageImageFromSelection({
              ...page,
              pageCandidates,
              selectedPageCandidateId: only.id,
              pageErrorMessage: undefined,
              pageCreationTaskId: creationTaskId,
            })
          }

          return {
            ...page,
            pageCandidates,
            selectedPageCandidateId: null,
            pageImageUrl: null,
            pageImageStatus: 'pending_selection' as const,
            pageErrorMessage: undefined,
            pageCreationTaskId: creationTaskId,
          }
        }),
      }
    }),
  }
}

export function selectPageCandidate(
  project: ComicProject,
  episodeId: string,
  pageId: string,
  candidateId: string,
): ComicProject {
  return {
    ...project,
    episodes: project.episodes.map((ep) => {
      if (ep.id !== episodeId) return ep
      return {
        ...ep,
        pages: ep.pages.map((page) => {
          if (page.id !== pageId) return page
          if (!page.pageCandidates.some((c) => c.id === candidateId)) return page
          return syncPageImageFromSelection({
            ...page,
            selectedPageCandidateId: candidateId,
          })
        }),
      }
    }),
  }
}

export function removePageCandidate(
  project: ComicProject,
  episodeId: string,
  pageId: string,
  candidateId: string,
): ComicProject {
  return {
    ...project,
    episodes: project.episodes.map((ep) => {
      if (ep.id !== episodeId) return ep
      return {
        ...ep,
        pages: ep.pages.map((page) => {
          if (page.id !== pageId) return page
          const pageCandidates = page.pageCandidates.filter((c) => c.id !== candidateId)
          let selectedPageCandidateId = page.selectedPageCandidateId
          if (!pageCandidates.some((c) => c.id === selectedPageCandidateId)) {
            selectedPageCandidateId = null
          }
          return syncPageImageFromSelection({
            ...page,
            pageCandidates,
            selectedPageCandidateId,
          })
        }),
      }
    }),
  }
}
